import { useState } from 'react'
import type { FleetTypeCount } from '@/api/hooks'
import { FleetComposition } from '@/components/game/FleetComposition'

/**
 * Tap-to-expand fleet-composition chip: a text pill ("N navires ⌄") that reveals
 * the per-type `FleetComposition` breakdown on click. Shared by `MissionCard`
 * (grid) and the dashboard `ProgressRow` (list) so the two surfaces don't diverge
 * on markup/a11y. Renders nothing for an empty fleet.
 *
 * No ship icon on the pill itself: a single icon can't represent a mixed fleet
 * (it was hardcoded to a brick, misleading for e.g. a frégate), and the leading
 * row icon + the per-type icons in the revealed breakdown already carry the
 * visual — the pill stays text-only to avoid icon overload, esp. in list mode.
 */
export function FleetCompositionChip({ fleet, idBase }: { fleet: FleetTypeCount[]; idBase: string }) {
  const [open, setOpen] = useState(false)
  if (fleet.length === 0) return null

  const totalShips = fleet.reduce((sum, f) => sum + f.count, 0)
  const panelId = `${idBase}-fleet`

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="text-[11px] px-2 py-1 rounded-full border border-sand-500/25 bg-ocean-900/60 text-sand-300 hover:bg-ocean-700 transition-colors inline-flex items-center gap-1.5"
      >
        <span className="tabular-nums font-semibold">{totalShips}</span>
        <span>{totalShips > 1 ? 'navires' : 'navire'}</span>
        <span aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>
      {open && (
        <div id={panelId} className="mt-1.5 rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2">
          <FleetComposition fleet={fleet} />
        </div>
      )}
    </div>
  )
}
